import { useEffect, useState } from "react";
import { message } from "antd";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import {
  getTtsVocabulary,
  patchTtsVocabulary,
  deleteTtsVocabulary,
} from "../service";
import { Input } from "V2.0/common/input";
import { Button } from "V2.0/common/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "V2.0/common/table";

// Maximum number of vocabulary words a user can add.
const MAX_ENTRIES = 100;

type VocabularyEntry = { word: string; replacement: string };

function entriesFromMap(map: Record<string, string> = {}): VocabularyEntry[] {
  return Object.entries(map).map(([word, replacement]) => ({
    word,
    replacement,
  }));
}

function extractError(e, fallback: string) {
  return (
    e?.response?.data?.msg ||
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    fallback
  );
}

export default function TtsVocabulary(props) {
  const { workspaceId, assistantId, locked } = props;

  const [entries, setEntries] = useState<VocabularyEntry[]>([]);
  const [word, setWord] = useState("");
  const [replacement, setReplacement] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // The word of the row currently being edited inline (null = none), plus its
  // draft values.
  const [editingWord, setEditingWord] = useState<string | null>(null);
  const [editWord, setEditWord] = useState("");
  const [editReplacement, setEditReplacement] = useState("");

  useEffect(() => {
    let active = true;

    async function loadVocabulary() {
      try {
        const response = await getTtsVocabulary({ workspaceId, assistantId });
        if (!active) return;
        setEntries(entriesFromMap(response.data?.ttsVocabulary));
      } catch (e) {
        if (active) message.error("Failed to load TTS vocabulary");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadVocabulary();

    return () => {
      active = false;
    };
  }, [workspaceId, assistantId]);

  // Adds a single new entry. PATCH merges it into the stored vocabulary, so
  // existing words are left untouched (no full-replace).
  async function handleAdd() {
    const trimmedWord = word.trim();
    const trimmedReplacement = replacement.trim();

    if (!trimmedWord || !trimmedReplacement) {
      message.error("Both the word and its replacement are required");
      return;
    }

    // The backend keys entries case-insensitively, so re-adding an existing
    // word updates it rather than creating a duplicate — mirror that here.
    const isExisting = entries.some(
      (entry) => entry.word.toLowerCase() === trimmedWord.toLowerCase()
    );

    if (!isExisting && entries.length >= MAX_ENTRIES) {
      message.error(`TTS vocabulary cannot exceed ${MAX_ENTRIES} words`);
      return;
    }

    setBusy(true);
    message.loading("Saving vocabulary");
    try {
      const response = await patchTtsVocabulary({
        workspaceId,
        assistantId,
        payload: { ttsVocabulary: { [trimmedWord]: trimmedReplacement } },
      });
      if (response.status === 200) {
        message.destroy();
        message.success(isExisting ? "Vocabulary updated" : "Vocabulary added");
        setEntries(entriesFromMap(response.data?.ttsVocabulary));
        setWord("");
        setReplacement("");
      }
    } catch (e) {
      message.destroy();
      message.error(extractError(e, "Failed to update vocabulary"));
    } finally {
      setBusy(false);
    }
  }

  // Removes a single word. DELETE only touches the listed word.
  async function handleRemove(targetWord: string) {
    setBusy(true);
    message.loading("Removing word");
    try {
      const response = await deleteTtsVocabulary({
        workspaceId,
        assistantId,
        payload: { words: [targetWord] },
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Word removed");
        setEntries(entriesFromMap(response.data?.ttsVocabulary));
      }
    } catch (e) {
      message.destroy();
      message.error(extractError(e, "Failed to remove word"));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(entry: VocabularyEntry) {
    setEditingWord(entry.word);
    setEditWord(entry.word);
    setEditReplacement(entry.replacement);
  }

  function cancelEdit() {
    setEditingWord(null);
    setEditWord("");
    setEditReplacement("");
  }

  // Persists an inline edit. When only the replacement changes, a single PATCH
  // is enough; when the word itself changes, the new word is PATCHed in and the
  // old key is DELETEd so it isn't left behind.
  async function saveEdit() {
    if (editingWord === null) return;

    const originalWord = editingWord;
    const newWord = editWord.trim();
    const newReplacement = editReplacement.trim();

    if (!newWord || !newReplacement) {
      message.error("Both the word and its replacement are required");
      return;
    }

    const wordChanged = newWord.toLowerCase() !== originalWord.toLowerCase();

    // Renaming onto another existing word would silently overwrite it — block it.
    if (
      wordChanged &&
      entries.some((entry) => entry.word.toLowerCase() === newWord.toLowerCase())
    ) {
      message.error(`"${newWord}" already exists`);
      return;
    }

    setBusy(true);
    message.loading("Updating vocabulary");
    try {
      let response = await patchTtsVocabulary({
        workspaceId,
        assistantId,
        payload: { ttsVocabulary: { [newWord]: newReplacement } },
      });

      if (wordChanged) {
        response = await deleteTtsVocabulary({
          workspaceId,
          assistantId,
          payload: { words: [originalWord] },
        });
      }

      if (response.status === 200) {
        message.destroy();
        message.success("Vocabulary updated");
        setEntries(entriesFromMap(response.data?.ttsVocabulary));
        cancelEdit();
      }
    } catch (e) {
      message.destroy();
      message.error(extractError(e, "Failed to update vocabulary"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col border rounded-lg w-3/5 min-w-[600px] m-4">
      <div className="p-4 bg-gray-50 rounded-t-lg px-6">
        <p className="text-base font-semibold text-gray-900">TTS Vocabulary</p>
        <p className="text-sm mt-1 text-gray-500">
          Control how the assistant pronounces specific words by mapping them to
          a phonetic or translated spelling (e.g. “Pune” → “पुणे”).
        </p>
      </div>

      <div className="flex flex-col gap-4 p-4 px-8 w-full">
        {!locked && (
          <div className="flex items-end gap-2 w-full">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-sm font-medium text-gray-700">Word</label>
              <Input
                value={word}
                placeholder="Word to replace"
                disabled={busy}
                onChange={(e) => setWord(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-sm font-medium text-gray-700">
                Replacement
              </label>
              <Input
                value={replacement}
                placeholder="How it should be spoken"
                disabled={busy}
                onChange={(e) => setReplacement(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
              />
            </div>
            <Button
              outline
              disabled={busy || !word.trim() || !replacement.trim()}
              onClick={handleAdd}
            >
              <PlusIcon />
              Add
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">
            No vocabulary added yet.
          </p>
        ) : (
          <Table dense grid>
            <TableHead>
              <TableRow>
                <TableHeader>Word</TableHeader>
                <TableHeader>Replacement</TableHeader>
                <TableHeader className="w-40 text-right">Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => {
                const isEditing = editingWord === entry.word;

                return (
                  <TableRow key={entry.word}>
                    <TableCell className="font-medium text-gray-900">
                      {isEditing ? (
                        <Input
                          value={editWord}
                          disabled={busy}
                          onChange={(e) => setEditWord(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                      ) : (
                        entry.word
                      )}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {isEditing ? (
                        <Input
                          value={editReplacement}
                          disabled={busy}
                          onChange={(e) => setEditReplacement(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                      ) : (
                        entry.replacement
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!locked && (
                        <div className="flex justify-end gap-2">
                          {isEditing ? (
                            <>
                              <Button disabled={busy} onClick={saveEdit}>
                                Save
                              </Button>
                              <Button
                                plain
                                disabled={busy}
                                onClick={cancelEdit}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                plain
                                disabled={busy}
                                onClick={() => startEdit(entry)}
                              >
                                Edit
                              </Button>
                              <Button
                                plain
                                disabled={busy}
                                onClick={() => handleRemove(entry.word)}
                              >
                                <TrashIcon className="text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
